// =============================================================================
// usePermissions — quyền hiệu lực của user đăng nhập.
//
// Trước đây: query trực tiếp `role_permissions` qua `profile.role` → KHÔNG
// nhận quyền từ position_permissions (vị trí). Nghĩa là sau khi admin gán
// user vào position có quyền X, UI vẫn không gate đúng.
//
// Bây giờ: gọi RPC `get_my_permissions()` (migration 20260603_0006) — union
// 2 nguồn position-based + role-based. Đồng bộ với `has_permission()` ở DB,
// nên UI gate khớp với RLS / RPC permission check.
//
// SCOPE-IN-NAME (Sprint 4 cutover): quyền có scope mang dạng `base:level`
// (vd order:view:branch). `has(base)` phải true khi user có CHÍNH `base`
// HOẶC bất kỳ biến thể base:{own,subordinates,branch,all} — mirror đúng
// `has_permission()` ở DB (migration 20260623_0001) để UI gate khớp RLS.
// =============================================================================

import { useQuery } from '@tanstack/react-query'
import type { PermissionName, PermissionScopeLevel, RoleName } from '@/types/db'
import { getSupabase } from '@/lib/supabase'
import { useProfile } from './use-profile'

// RPC get_my_permissions() trả thêm `scope` (cột legacy) từ migration
// 20260610_0004. Với row biến thể scope-in-name, scope nằm trong TÊN nên cột
// này vô nghĩa (đặt 'all'); scope thật suy ra từ hậu tố tên.
type PermissionRow = { permission_name: string; scope?: string | null }

// 4 mức scope của mô hình mới (theo Nobi). Rank = rộng dần để lấy MAX.
const SCOPE_RANK: Record<PermissionScopeLevel, number> = {
  own: 1,
  subordinates: 2,
  branch: 3,
  all: 4,
  // 'source' KHÔNG nằm trên trục own→all (chỉ lọc theo NGUỒN đơn) → rank 0 để
  // KHÔNG tham gia MAX: scopeOf() trả null, nhưng splitScope vẫn nhận ':source' là
  // hậu tố hợp lệ nên has('order:view') vẫn TRUE cho user chỉ có order:view:source.
  source: 0,
}
const SCOPE_SUFFIXES = Object.keys(SCOPE_RANK) as PermissionScopeLevel[]
const RANK_TO_LEVEL: PermissionScopeLevel[] = ['own', 'subordinates', 'branch', 'all']

// Tách `base:level` → { base, level }. Không có hậu tố scope hợp lệ → level null
// (quyền không-scope như product:manage, hoặc tên base legacy).
function splitScope(name: string): {
  base: string
  level: PermissionScopeLevel | null
} {
  const idx = name.lastIndexOf(':')
  if (idx <= 0) return { base: name, level: null }
  const suffix = name.slice(idx + 1)
  if ((SCOPE_SUFFIXES as string[]).includes(suffix)) {
    return { base: name.slice(0, idx), level: suffix as PermissionScopeLevel }
  }
  return { base: name, level: null }
}

// Cột scope legacy (DataScope) → mức scope-in-name. 'department' map về 'branch'
// (mô hình mới không có mức phòng ban) — khớp data-convert ở DB.
function legacyScopeToLevel(scope?: string | null): PermissionScopeLevel {
  switch (scope) {
    case 'own':
      return 'own'
    case 'subordinates':
      return 'subordinates'
    case 'department':
      return 'branch'
    case 'all':
    default:
      return 'all'
  }
}

export function usePermissions() {
  const profileQuery = useProfile()
  const role = profileQuery.data?.role as RoleName | undefined
  const userId = profileQuery.data?.id

  const permsQuery = useQuery({
    // Key bao gồm userId để khi đổi user (vd logout/login) cache reset.
    queryKey: ['permissions', 'effective', userId ?? null] as const,
    enabled: Boolean(userId),
    queryFn: async (): Promise<PermissionRow[]> => {
      const { data, error } = await getSupabase().rpc('get_my_permissions')
      if (error) throw error
      return (data ?? []) as PermissionRow[]
    },
    // Quyền hiệu lực có thể đổi runtime khi admin (ở session khác) gán/gỡ
    // position của user đang đăng nhập. Trước đây cache 5 phút làm UI gate
    // không kịp sync. Giảm xuống 30s + refetch khi quay lại tab để cấu hình
    // quyền của admin phản ánh đúng trên UI user trong vòng tối đa 30s.
    // Mutation hooks (use-user-positions, use-positions) vẫn invalidate ngay
    // cho admin đang thao tác.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })

  const rows = permsQuery.data ?? []

  // Tập tên quyền HIỆU LỰC để gate: tên thật + base của mỗi biến thể
  // scope-in-name → has('order:view') true khi chỉ có 'order:view:branch'.
  const effective = new Set<string>()
  // Rank scope rộng nhất theo base (cho scopeOf — scope enforcement nằm ở RLS;
  // giữ chính xác cho consumer tương lai như trang Nhân viên).
  const rankByBase = new Map<string, number>()

  for (const r of rows) {
    effective.add(r.permission_name)
    const { base, level } = splitScope(r.permission_name)
    if (level) effective.add(base) // base ⇐ biến thể
    const effectiveLevel = level ?? legacyScopeToLevel(r.scope)
    rankByBase.set(base, Math.max(rankByBase.get(base) ?? 0, SCOPE_RANK[effectiveLevel]))
  }

  return {
    role,
    permissions: rows.map((r) => r.permission_name as PermissionName),
    loading: profileQuery.isLoading || permsQuery.isLoading,
    has: (perm: PermissionName) => effective.has(perm),
    // Scope dữ liệu user có cho 1 quyền base; null nếu không có quyền.
    scopeOf: (perm: PermissionName): PermissionScopeLevel | null => {
      const rank = rankByBase.get(perm)
      return rank ? RANK_TO_LEVEL[rank - 1] : null
    },
  }
}
